import * as Sentry from '@sentry/browser'
import { ROUTES } from 'config/routes'
import _ from 'lodash'
import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, withRouter } from 'react-router-dom'
import { compose } from 'redux'
import { createStructuredSelector } from 'reselect'

import Toast from 'components/base/Toast'
import CustomMetaTag from 'components/CustomMetaTag'
import LoadingIndicator from 'components/LoadingIndicator'
import MaintainModal from 'components/MaintainModal'
import { EnvironmentNames, USE_DEV_CONSOLE, VERSION, VERSION_STORAGE_KEY } from 'constants/App'
import { ReloadTypes } from 'constants/Notice'
import { createInputLogger } from 'containers/App/InputLogger'
import { fetchAnything } from 'containers/App/reducers'
import BookingWorkRecordRouting from 'containers/BookingWorkRecord/routing'
import ConfirmBooking from 'containers/ConfirmBooking/Loadable'
import ForbiddenPage from 'containers/ForbiddenPage/Loadable'
import IntegratedReceptionRouting from 'containers/IntegratedReception/IntegratedReceptionRouting'
import LoginPage from 'containers/LoginPage/Loadable'
import NotFoundPage from 'containers/NotFoundPage/Loadable'
import PhoneBooking from 'containers/PhoneBooking/Loadable'
import ReceiptBooking from 'containers/ReceiptBooking/Loadable'
import Modal from 'containers/Reception/components/Modal'
import { Backdrop } from 'containers/Reception/components/utils'
import ReceptionRouting from 'containers/Reception/ReceptionRouting'
import AppPrivateWrapper from 'containers/Routes/AppPrivateWrapper'
import { SpBerthManagementPage } from 'containers/SpBerthManagement'
import SpDeliveryReportPage from 'containers/SpDeliveryReport'
import { SpDeliveryWorkPage } from 'containers/SpDeliveryWork'
import { SpDeliveryWorkAttachmentPreviewPage } from 'pages/SpDeliveryWorkAttachmentPreview'
import { SpDeliveryListPage } from 'containers/SpDeliveryList'
import { buildReloadConfirmationModal, extractReloadNotice, reloadBrowser } from 'utils/ForceReload'
import { setHistory } from 'utils/history'
import injectReducer from 'utils/injectReducer'
import injectSaga from 'utils/injectSaga'
import loginStorage from 'utils/loginStorage'

import { getAuthenticationData, getUserData } from './actions'
import { context, UPDATE_NOTICES } from './constants'
import reducer, { addDebugLog, openModal, updateWindowSize } from './reducers'
import saga from './sagas'
import {
  appLoadingSelectors,
  baseSelector,
  isOpenModalMaintainSelectors,
  selectDebugLogs,
  selectNotices,
  userDataSelectors,
} from './selectors'
import watchNotice, { NoticeStorage } from './watchNotice'

const ONE_MINUTE = 1000 * 60

const IS_LOCAL = process.env.REACT_APP_ENV === EnvironmentNames.LOCAL
const LIMIT = IS_LOCAL ? 10 * 1000 : 3 * ONE_MINUTE
const INTERVAL = IS_LOCAL ? 1000 : 1000 * 10

let isFirstError = true
let noticeCount = 0

class App extends React.Component {
  logger = null

  constructor(props) {
    super(props)
    this.logger = createInputLogger(INTERVAL, LIMIT, (isForce, forceLogout) => {
      if (isForce) {
        reloadBrowser(forceLogout)
        return
      }

      this.props.openModal(buildReloadConfirmationModal(forceLogout))
    })

    if (process.env.REACT_APP_ENV !== EnvironmentNames.PRODUCTION) {
      this.logger.timerDebug = this.props.addDebugLog
    }
  }

  componentDidMount() {
    const setVariableVh = () => {
      // Calculator vh on touch devices and set variable --vh
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    setVariableVh()
    window.addEventListener('resize', _.debounce(setVariableVh, 200))

    window.onerror = this.onError
    window.addEventListener(
      'resize',
      _.debounce((e) => {
        const { pathname } = global.location
        if (!pathname.startsWith('/reception-custom') && !pathname.startsWith('/receipt-booking'))
          return
        this.props.updateWindowSize(e.target.innerHeight)
      })
    )

    const sessionData = loginStorage.getAuthData()

    if (sessionData) {
      this.props.fetchAnything({
        url: '/reception_notification',
        storePath: 'base/receptionNotificationSound',
      })
    }

    const oldVersion = localStorage.getItem(VERSION_STORAGE_KEY)
    this.props.addDebugLog(`prev version: ${oldVersion}, next version: ${VERSION}`)

    if (VERSION !== oldVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, VERSION)
    }

    setHistory(this.props.history)

    if (process.env.REACT_APP_ENV === EnvironmentNames.LOCAL) return

    // お知らせを監視
    watchNotice(
      (notices, revision) => {
        this.props.updateNotices({ notices })

        const foundNotice = extractReloadNotice(notices)

        if (!foundNotice) return

        const { base } = this.props
        const { reload } = foundNotice

        switch (reload.type) {
          case ReloadTypes.FORCE: {
            this.props.addDebugLog(`start force reload ${JSON.stringify(reload)}`)
            this.logger.startLog(true, reload.forceLogout)
            break
          }
          case ReloadTypes.OPTION: {
            this.props.addDebugLog(`start option reload ${JSON.stringify(reload)}`)
            if (!base.modalOptions.isOpen) {
              this.props.openModal(buildReloadConfirmationModal(reload.forceLogout))
            }
            break
          }
          default:
            console.assert(false)
        }

        if (revision) this.props.addDebugLog(`notice revision: ${revision}`)
      },
      () => noticeCount
    )

    this.props.addDebugLog(`notice revision: ${NoticeStorage.getRevision()}`)
  }

  onError = (message, source, lineno, colno, error) => {
    // iOS Safari/WKWebViewの既知のバグによるエラーをフィルタリング
    // https://github.com/getsentry/sentry-javascript/issues/2342
    const messageStr = String(message || '')
    if (
      messageStr.includes('NotFoundError') &&
      messageStr.includes('The object can not be found here')
    ) {
      return
    }

    const errorMessage = [
      `Message: ${message}`,
      `Source: ${source}`,
      `Line: ${lineno}`,
      `Column: ${colno}`,
    ].join('\n')

    // Sentryへのエラー送信
    if (process.env.REACT_APP_SENTRY_DSN && error) {
      Sentry.captureException(error, {
        tags: {
          type: 'global_javascript_error',
        },
        extra: {
          message,
          source,
          lineno,
          colno,
        },
      })
    }

    // 連続して alert が表示されるのを防ぐ
    if (!isFirstError) {
      return
    }

    isFirstError = false

    if (process.env.REACT_APP_ENV !== EnvironmentNames.PRODUCTION) {
      console.error(error)
    }

    alert(errorMessage)
  }

  render() {
    const { base, closeModal, closeToast, notices, debugLogs, isOpenModalMaintain } = this.props
    const { loading, modalOptions, toastOptions } = base

    noticeCount = notices.length

    if (isOpenModalMaintain) {
      return <MaintainModal isOpen />
    }

    return (
      <div>
        <CustomMetaTag />
        <Switch>
          <Route path={ROUTES.login} component={LoginPage} />
          <Route path={ROUTES.confirmBooking} component={ConfirmBooking} />
          <Route path={ROUTES.generalReception} component={ReceiptBooking} />
          <Route path={ROUTES.receiptBookingExit} component={ReceiptBooking} />
          <Route path={ROUTES.generalReceptionDeskExit} component={ReceiptBooking} />
          <Route path={ROUTES.recordBookingWorkLog} component={BookingWorkRecordRouting} />
          <Route path={ROUTES.receiptBooking} component={ReceptionRouting} />
          <Route path={ROUTES.generalReceptionDesk} component={IntegratedReceptionRouting} />
          <Route path={ROUTES.spDeliveryList} component={SpDeliveryListPage} />
          <Route path={ROUTES.spBerthManagement} component={SpBerthManagementPage} />
          <Route exact path={ROUTES.spDeliveryReport} component={SpDeliveryReportPage} />
          <Route exact path={ROUTES.spDeliveryReportWork} component={SpDeliveryWorkPage} />
          <Route
            exact
            path={ROUTES.spDeliveryWorkAttachmentPreview}
            component={SpDeliveryWorkAttachmentPreviewPage}
          />
          <Route path={ROUTES.sp} component={PhoneBooking} />
          <Route path={ROUTES.mtenants} component={ReceptionRouting} />
          <Route path={ROUTES.forbidden} component={ForbiddenPage} />
          <Route
            path={ROUTES.root}
            render={(props) => <AppPrivateWrapper {...this.props} {...props} />}
          />
          <Route component={NotFoundPage} />
        </Switch>

        {/* Loader */}
        {loading && (
          <Fragment>
            <Backdrop style={{ zIndex: 101 }} />
            <LoadingIndicator />
          </Fragment>
        )}

        {/* Modal */}
        <Modal
          isOpen={modalOptions.isOpen}
          content={modalOptions.content}
          buttons={modalOptions.buttons}
          closeModal={closeModal}
        />

        {/* Toast */}
        {toastOptions.isOpen && <Toast content={toastOptions.content} closeToast={closeToast} />}

        {/* Dev Console */}
        {USE_DEV_CONSOLE && (
          <div
            className="bg-gray m-2"
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: 600,
              height: 300,
              opacity: 0.8,
              overflowY: 'scroll',
            }}
          >
            {debugLogs.map((log, index) => (
              <div key={index}>{log}</div>
            ))}
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  userData: userDataSelectors(),
  loading: appLoadingSelectors(),
  base: baseSelector(),
  notices: selectNotices(),
  debugLogs: selectDebugLogs(),
  isOpenModalMaintain: isOpenModalMaintainSelectors(),
})

const matchDispatchToProps = (dispatch) => ({
  getAuthenticationData: (data) => dispatch(getAuthenticationData(data)),
  getUserData: () => dispatch(getUserData()),
  closeModal: () => dispatch({ type: 'CLOSE_MODAL' }),
  closeToast: () => dispatch({ type: 'CLOSE_TOAST' }),
  updateWindowSize: (height) => dispatch(updateWindowSize(height)),
  updateNotices: (payload) => {
    dispatch({ type: UPDATE_NOTICES, payload })
  },
  openModal: (data) => dispatch(openModal(data)),
  addDebugLog: (data) => dispatch(addDebugLog(data)),
  fetchAnything: (data) => dispatch(fetchAnything(data)),
})

const withReducer = injectReducer({ key: context, reducer })
const withSaga = injectSaga({ key: context, saga })
const withConnect = connect(mapStateToProps, matchDispatchToProps)

export default compose(withRouter, withReducer, withSaga, withConnect)(App)
